import React, {
  MouseEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import classNames from 'classnames';
import { useLocation } from 'react-router';

import Button from 'components/Button';
import Drawer from 'components/Drawer';
import FollowSpeaker from 'components/FollowSpeaker';
import IconFemtasy from 'components/IconFemtasy';
import OrgasmLine from 'components/Icons/Svg/OrgasmLine';
import OrgasmSolid from 'components/Icons/Svg/OrgasmSolid';
import Image from 'components/Image';
import Loader from 'components/Loader';
import LoveIcon from 'components/LoveIcon';
import Message from 'components/Message';
import NestedLink from 'components/NestedLink';
import PlayButton from 'components/PlayButton';
import SpeakerTag from 'components/SpeakerTag';
import StoryContentCategoryLabel from 'components/StoryContentCategoryLabel';
import TagPillList from 'components/TagPillList';
import Typography from 'components/Typography';

import { orgasmIconPositions } from 'containers/StoryDetails/Player/helpers';
import OrgasmsTooltip from 'containers/StoryDetails/Player/OrgasmsTooltip';
import QueueList from 'containers/StoryDetails/Player/QueueList';

import { useGetStoryForPlayerQuery } from 'graphql/generated/graphql';

import { useAuth } from 'hooks/useAuth';
import { FeatureFlag, useFeatureFlags } from 'hooks/useFeatureFlags';
import useGraphaelDefaults from 'hooks/useGraphaelDefaults';
import useHiddenWithCookie from 'hooks/useHiddenWithCookie';
import { usePlayer } from 'hooks/usePlayer';
import { PlayNextResult, useQueue } from 'hooks/useQueue';
import { useTracking } from 'hooks/useTracking';

import { PLAY_NEXT_PROMO_COOKIE } from 'store/consts';

import { ContentType } from 'types/contentType';

import openLockImage from '../images/openLock.svg';

import ProgressBar from './ProgressBar';

import './style.scss';

const PlaylistButton = ({
  direction,
  hidden,
  disabled,
  onClick,
}: {
  direction: 'next' | 'previous';
  hidden: boolean;
  disabled: boolean;
  onClick: MouseEventHandler;
}) => (
  <button
    className={classNames(`Player__arrow Player__arrow--${direction}`, {
      'Player__arrow--disabled': disabled,
      'Player__arrow--hidden': hidden,
    })}
    aria-label={`play ${direction}`}
    onClick={onClick}
    disabled={disabled}
  >
    <IconFemtasy icon="Nextline" size="24" />
  </button>
);

const PlayerControls = () => {
  const [queueListOpened, setQueueListOpen] = useState(false);
  const [, setPermanentlyHidden] = useHiddenWithCookie(PLAY_NEXT_PROMO_COOKIE);

  const { trackContentInteraction } = useTracking();
  const {
    currentlyPlaying,
    isLoading,
    isPlaying,
    requestSeekBackward,
    requestSeekForward,
    requestSeekToTime,
    togglePlay,
    fullAudioPlayed,
    mediaDuration,
    currentTime,
  } = usePlayer();
  const { hasNext, hasPrevious, hasStories, playPreviousStory, playNextStory } =
    useQueue();

  const seekBackward = React.useCallback(() => {
    requestSeekBackward();
    trackContentInteraction(
      'seek_backward',
      'player',
      `story_${currentlyPlaying?.id}`,
      currentlyPlaying?.sourceUrl,
    );
  }, [currentlyPlaying, requestSeekBackward, trackContentInteraction]);

  const seekForward = React.useCallback(() => {
    requestSeekForward();
    trackContentInteraction(
      'seek_forward',
      'player',
      `story_${currentlyPlaying?.id}`,
      currentlyPlaying?.sourceUrl,
    );
  }, [currentlyPlaying, requestSeekForward, trackContentInteraction]);

  const handlePlayPrevious = React.useCallback(() => {
    trackContentInteraction(
      'previous_audio',
      'player',
      `story_${currentlyPlaying?.id}`,
    );
    playPreviousStory();
  }, [currentlyPlaying, playPreviousStory, trackContentInteraction]);

  const handlePlayNext = React.useCallback(() => {
    setPermanentlyHidden(true);

    const result = playNextStory();

    if (result === PlayNextResult.NextStoryPlayed) {
      trackContentInteraction(
        'next_audio',
        'player',
        `story_${currentlyPlaying?.id}`,
      );
    }
  }, [
    currentlyPlaying?.id,
    playNextStory,
    setPermanentlyHidden,
    trackContentInteraction,
  ]);

  const togglePlayback = React.useCallback(() => {
    togglePlay();
    trackContentInteraction(
      isPlaying ? 'pause' : 'continue',
      'player',
      `story_${currentlyPlaying?.id}`,
      currentlyPlaying?.sourceUrl,
    );
  }, [currentlyPlaying, isPlaying, togglePlay, trackContentInteraction]);

  const orgasmsPositions = useMemo(() => {
    if (mediaDuration && currentlyPlaying?.orgasms) {
      return orgasmIconPositions({
        mediaDuration,
        orgasms: currentlyPlaying?.orgasms,
        minGap: 7,
      });
    } else return [];
  }, [currentlyPlaying?.orgasms, mediaDuration]);

  const areWePastOrgasm = useCallback(
    (index: number) => {
      if (!currentlyPlaying?.orgasms) return false;

      return currentTime >= currentlyPlaying?.orgasms[index];
    },
    [currentTime, currentlyPlaying?.orgasms],
  );

  const goToOrgasm = useCallback(
    (index) => {
      if (currentlyPlaying?.orgasms) {
        requestSeekToTime(currentlyPlaying?.orgasms[index]);
        trackContentInteraction(
          'click',
          'orgasm-indicator',
          `story_${currentlyPlaying?.id}_orgasm_${currentlyPlaying?.orgasms[index]}`,
        );
      }
    },
    [currentlyPlaying, requestSeekToTime, trackContentInteraction],
  );

  const firstFireIcon = useRef<HTMLDivElement>(null);

  return (
    <div
      className={classNames('Player__playerControls', {
        Player__playerControls__withOrgasms: orgasmsPositions.length > 0,
      })}
    >
      {orgasmsPositions.length > 0 && (
        <div className="Player__orgasms">
          {orgasmsPositions.map((position, index) => (
            <div
              data-testid="orgasm-indicator-wrapper"
              key={index}
              className="Player__orgasm"
              style={{
                left: `${position}%`,
              }}
            >
              <div
                ref={index === 0 ? firstFireIcon : undefined}
                data-testid="orgasm-indicator"
                className="Player__orgasm_wrapper"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  goToOrgasm(index);
                }}
              >
                {areWePastOrgasm(index) ? <OrgasmSolid /> : <OrgasmLine />}
              </div>
            </div>
          ))}
          <OrgasmsTooltip anchorEl={firstFireIcon} />
        </div>
      )}

      <ProgressBar />
      <div className="Player__buttons">
        <PlaylistButton
          hidden={!hasStories()}
          disabled={!hasPrevious()}
          direction={'previous'}
          onClick={handlePlayPrevious}
        />
        <button
          className="Player__arrow Player__arrow--rewind"
          onClick={seekBackward}
        >
          <IconFemtasy icon="15sec-bckwrds" size="40" />
        </button>
        <PlayButton
          size="64"
          alreadyListened={fullAudioPlayed}
          isPlaying={isPlaying}
          isLoading={isLoading}
          onClick={togglePlayback}
        />
        <button
          className="Player__arrow Player__arrow--forward"
          onClick={seekForward}
        >
          <IconFemtasy icon="15sec-frwrds" size="40" />
        </button>
        <PlaylistButton
          hidden={!hasStories()}
          disabled={!hasNext()}
          direction={'next'}
          onClick={handlePlayNext}
        />
      </div>
      <div className="Player__bottom">
        <div
          className="Player__openQueue"
          onClick={() => {
            setQueueListOpen(true);
            trackContentInteraction(
              'click',
              'open-queue-button',
              `open-queue-button_${currentlyPlaying?.id}`,
            );
          }}
        >
          <Typography variant="small-thick">
            <Message message={{ id: 'SM0123.Player.queue_link' }} />
          </Typography>
          <IconFemtasy icon="View-queue" />
        </div>
      </div>
      {queueListOpened && <QueueList onClose={() => setQueueListOpen(false)} />}
    </div>
  );
};

interface IProps {
  storyId: string;
}

const Player: React.FC<IProps> = ({ storyId }) => {
  const { isAuthenticated, hasPremiumAccess } = useAuth();

  const { hideFullPlayer } = usePlayer();
  const { hasStories } = useQueue();
  const { isFeatureFlagEnabled } = useFeatureFlags();

  const { pathname } = useLocation();

  useEffect(() => {
    return () => {
      hideFullPlayer();
    };
    // the `pathname` is not a direct dependency but it's used to hide the
    // player on every page change
  }, [hideFullPlayer, pathname]);

  const { data } = useGetStoryForPlayerQuery({
    ...useGraphaelDefaults(),
    variables: { storyId },
  });

  const isLoading = !data?.story && hasStories();

  if (isLoading) {
    return (
      <Drawer
        anchor="bottom"
        icon="Arrowdown"
        onClose={() => hideFullPlayer()}
        header=""
        ariaLabel={{ id: 'components.Account.Player' }}
        open={true}
      >
        <div className="Player">
          <div className="Player__content">
            <Loader />
          </div>
        </div>
      </Drawer>
    );
  }

  if (data && data.story) {
    const story = data.story;
    const isSeries = story.contentType.slug === ContentType.Series;

    return (
      <Drawer
        anchor="bottom"
        icon="Arrowdown"
        onClose={() => hideFullPlayer()}
        header={story.title}
        open={true}
      >
        <div className="Player">
          <div className="Player__content">
            <div className="Player__speakerLabel">
              <SpeakerTag speaker={story.speaker} />
              {isAuthenticated && (
                <>
                  <FollowSpeaker
                    speaker={story.speaker}
                    matomoName="story_details_speaker_follow"
                  />
                  {!isSeries && (
                    <LoveIcon
                      itemId={story.id}
                      loved={story.loved}
                      playlists={story.playlists}
                    />
                  )}
                </>
              )}
            </div>
            <div className="Player__categoryAndTheme">
              <StoryContentCategoryLabel contentType={story.contentType} />
              {story.themeTag && (
                <div>
                  <span>&nbsp;-&nbsp;</span>
                  <TagPillList className="themeTag" tags={[story.themeTag]} />
                </div>
              )}
            </div>
            {!isSeries && !pathname.endsWith(`${storyId}/details`) && (
              <div className="Player__fullDescriptionLink">
                <NestedLink to={`${pathname}/${storyId}/details`}>
                  <Typography variant="link">
                    <Message
                      message={{
                        id: 'components.StoryDetails.readFullDescription',
                      }}
                    />
                  </Typography>
                </NestedLink>
              </div>
            )}
            <div className="Player__tags">
              <TagPillList
                className="storyTags"
                tags={story.tags}
                tagToExclude={story.themeTag}
              />
            </div>
            {!hasPremiumAccess && (
              <div className="Player__unlock">
                <Button
                  title={{
                    id: isFeatureFlagEnabled(FeatureFlag.UNLOCK_ALL_AUDIOS)
                      ? 'features.unlockAllAudios.cta'
                      : 'components.StoryDetails.unlock',
                  }}
                  linkTo="/sign-up"
                  icon={{
                    component: (
                      <Image
                        src={openLockImage}
                        alt={{ id: 'components.Player.UnlockAudio.alt' }}
                      />
                    ),
                  }}
                />
              </div>
            )}
          </div>
        </div>

        <PlayerControls />
      </Drawer>
    );
  }

  return <></>;
};

export default Player;
